import { BookingGate } from "../components/BookingGate";
import { PageLinks } from "../components/PageLinks";
import { Rule } from "../components/Rule";
import { Reveal } from "../components/Reveal";
import { pages, travelPage } from "../content/wedding";

/**
 * A decision-first travel guide: stay, fly, and get around. The compact
 * overview gives guests the three answers immediately; the numbered sections
 * underneath carry the detail they need to book.
 */
export function Travel() {
  const { glance, lodging, gettingHere, gettingAround, help } = travelPage;

  return (
    <main class="page motion-quiet page--tight travel-page" id="top">
      <section class="section travel-hero">
        <div class="shell">
          <div class="travel-hero__copy">
            <Reveal class="section-head">
              <span class="eyebrow">{pages.travel.eyebrow}</span>
              <h1>{pages.travel.title}</h1>
            </Reveal>
            <Reveal class="prose" delay={60}>
              <p>{pages.travel.lead}</p>
            </Reveal>
          </div>

          <div class="travel-glance">
            {glance.map((item, index) => (
              <Reveal
                as="article"
                class="travel-glance__item"
                delay={90 + index * 70}
                key={item.eyebrow}
              >
                <span class="eyebrow">{item.eyebrow}</span>
                <p class="travel-glance__value">{item.title}</p>
                <p class="travel-glance__note">{item.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section class="section section--ruled" id="lodging">
        <Rule class="section__seam" />
        <div class="shell">
          <TravelSectionHead
            eyebrow={lodging.eyebrow}
            title={lodging.title}
            lead={lodging.lead}
          />

          <Reveal as="article" class="travel-featured">
            <div class="travel-featured__main">
              <span class="eyebrow">{lodging.featured.eyebrow}</span>
              <h3>{lodging.featured.name}</h3>
              <p class="travel-location">{lodging.featured.location}</p>
              <p class="travel-featured__description">
                {lodging.featured.description}
              </p>
              <ul class="travel-features">
                {lodging.featured.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>

            <aside class="travel-featured__booking">
              <span class="eyebrow">{lodging.featured.booking.eyebrow}</span>
              <h4>{lodging.featured.booking.title}</h4>
              <p>{lodging.featured.booking.body}</p>
              {/* Not an anchor: the link is not in this page for a crawler
                  to take. See `BookingGate`. */}
              <BookingGate />
            </aside>
          </Reveal>

          <div class="travel-alternates">
            <Reveal as="h3" class="travel-alternates__heading">
              {lodging.alternatesHeading}
            </Reveal>
            <div class="travel-hotels">
              {lodging.alternates.map((hotel, index) => (
                <Reveal
                  as="article"
                  class="travel-hotel"
                  delay={index * 70}
                  key={hotel.name}
                >
                  <div class="travel-hotel__head">
                    <div>
                      <h4>{hotel.name}</h4>
                      <p class="travel-location">{hotel.location}</p>
                    </div>
                    <span class="travel-tag">{hotel.tag}</span>
                  </div>
                  <p class="travel-hotel__description">{hotel.description}</p>
                  <p class="travel-hotel__best">
                    <span class="travel-hotel__label">Best for</span>
                    {hotel.bestFor}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal as="aside" class="travel-note">
            <h3>{lodging.note.title}</h3>
            <p>{lodging.note.body}</p>
          </Reveal>
        </div>
      </section>

      <section class="section section--ruled" id="getting-here">
        <Rule class="section__seam" />
        <div class="shell">
          <TravelSectionHead
            eyebrow={gettingHere.eyebrow}
            title={gettingHere.title}
            lead={gettingHere.lead}
          />

          <div class="travel-airports">
            {gettingHere.airports.map((airport, index) => (
              <Reveal
                as="article"
                class="travel-airport"
                delay={index * 70}
                key={airport.code}
              >
                <span class="eyebrow">{airport.code}</span>
                <h3>{airport.name}</h3>
                <p>{airport.body}</p>
                <ul class="travel-tags">
                  {airport.options.map((option) => (
                    <li key={option}>{option}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>

          <Reveal as="article" class="travel-silver">
            <span class="eyebrow">{gettingHere.silverLine.eyebrow}</span>
            <h3>{gettingHere.silverLine.title}</h3>
            <p>{gettingHere.silverLine.body}</p>
            <ol
              class="travel-route"
              aria-label={gettingHere.silverLine.ariaLabel}
            >
              {gettingHere.silverLine.stops.map((stop, index) => (
                <li class="travel-route__step" key={stop.title}>
                  {index > 0 && (
                    <span class="travel-route__arrow" aria-hidden="true">
                      &rarr;
                    </span>
                  )}
                  <span class="travel-route__stop">
                    <strong>{stop.title}</strong>
                    <span>{stop.detail}</span>
                  </span>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      <section class="section section--ruled" id="getting-around">
        <Rule class="section__seam" />
        <div class="shell">
          <TravelSectionHead
            eyebrow={gettingAround.eyebrow}
            title={gettingAround.title}
          />

          <div class="travel-choices">
            {gettingAround.choices.map((choice, index) => (
              <Reveal
                as="article"
                class="travel-choice"
                delay={index * 70}
                key={choice.title}
              >
                <span class="eyebrow">{choice.eyebrow}</span>
                <h3>{choice.title}</h3>
                <ul>
                  {choice.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>

          <div class="travel-notes">
            {gettingAround.notes.map((note, index) => (
              <Reveal as="article" delay={index * 70} key={note.eyebrow}>
                <span class="eyebrow">{note.eyebrow}</span>
                <p>{note.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section class="section section--ruled travel-help">
        <Rule class="section__seam" />
        <div class="shell">
          <Reveal class="travel-help__copy">
            <div class="section-head">
              <span class="eyebrow">{help.eyebrow}</span>
              <h2>{help.title}</h2>
            </div>
            <p>{help.body}</p>
          </Reveal>
        </div>
      </section>

      <PageLinks />
    </main>
  );
}

function TravelSectionHead({
  eyebrow,
  title,
  lead,
}: {
  eyebrow: string;
  title: string;
  lead?: string;
}) {
  return (
    <div class="intro">
      <Reveal class="section-head">
        <span class="eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
      </Reveal>
      {lead && (
        <Reveal class="prose" delay={60}>
          <p>{lead}</p>
        </Reveal>
      )}
    </div>
  );
}
